import { alpha, Box, type SxProps, type Theme } from '@mui/material';

/**
 * 契約一覧・契約詳細の表で共通に使う部品。
 * 行を低くして一度に多く示し、幅が足りないときは識別の列を固定して横にスクロールする。
 */

/** 表の余白。テーマのセルの余白（上下12px）を詰めて1行を低くし、左右は16px（列の間は32px）のゆとりを残す */
export const compactTableSx: SxProps<Theme> = {
  '& .MuiTableCell-root': { py: 0.75, px: 2 },
  '& .MuiTableCell-head': { py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' },
  '& .MuiTableCell-body': { whiteSpace: 'nowrap' },
};

/** 見出し。単位は2段目に示し、見出しが列の途中で折り返さないようにする */
export function HeaderLabel({ name, unit }: { name: string; unit?: string }) {
  return (
    <>
      <Box component="span" display="block">
        {name}
      </Box>
      {unit && (
        <Box component="span" display="block" fontWeight="normal">
          {unit}
        </Box>
      )}
    </>
  );
}

const STICKY_CELL = 'sticky-cell';

/**
 * 横スクロールしても動かない列。値の行では、下を流れる列を隠すため背景を塗る。
 * edge は、固定する列のうち流れる列と接する列で、境目に線を引く
 */
export function stickyCell(
  side: 'left' | 'right',
  offset: number,
  width: number | { min: number },
  options: { header?: boolean; edge?: boolean } = {},
): { className?: string; sx: SxProps<Theme> } {
  return {
    className: options.header ? undefined : STICKY_CELL,
    sx: {
      position: 'sticky',
      [side]: offset,
      // 最小の幅だけを決めた列は、中身の幅に合わせる（width: 1% で、余った幅を受け取らない）。
      // 表のセルには最大の幅が効かないため、長すぎる中身は、中身の側で幅を抑えて省略する
      ...(typeof width === 'number'
        ? { width, minWidth: width, maxWidth: width }
        : { width: '1%', minWidth: width.min }),
      zIndex: options.header ? 3 : 1,
      ...(options.header ? {} : { bgcolor: 'background.paper' }),
      ...(options.edge
        ? side === 'left'
          ? { borderRight: 1, borderRightColor: 'divider' }
          : { borderLeft: 1, borderLeftColor: 'divider' }
        : {}),
    },
  };
}

/**
 * 中身の幅に合わせる列（余った幅を受け取らない）。余った幅は、表の右端の空の列が受け取る。
 * 最小の幅で、列と列の間にゆとりを持たせ、右寄せの数値と隣の列が詰まって見えないようにする
 */
export const fitColumnSx = (minWidth: number) => ({ width: '1%', minWidth }) as const;

/**
 * 固定した列の背景（TableRow の sx に渡す）。
 * 固定した列は、横にスクロールした列の上に重なるので、背景をいつも不透明の白にし、その上に行の色を重ねる。
 * 行の色（マウスオーバー・選択）は半透明のことがあり、そのまま使うと下の列が透けて見えるため
 */
export const stickyRowSx = (t: Theme) => {
  const selected = alpha(t.palette.primary.main, t.palette.action.selectedOpacity);
  const selectedHover = alpha(
    t.palette.primary.main,
    t.palette.action.selectedOpacity + t.palette.action.hoverOpacity,
  );
  return {
    '--row-overlay': 'transparent',
    // テーマの行のマウスオーバーの色（AppProviders の MuiTableRow）
    '&:hover': { '--row-overlay': '#f1f5f9' },
    '&.Mui-selected': { '--row-overlay': selected },
    '&.Mui-selected:hover': { '--row-overlay': selectedHover },
    [`& .${STICKY_CELL}`]: {
      backgroundImage: 'linear-gradient(var(--row-overlay), var(--row-overlay))',
    },
  };
};
